/** KAN-722: префиксы меток Telegram-аккаунтов: список, изменения, подбор по номеру. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { normalizePhone, validatePhone } from '@/lib/telegramConnect';
import { telegramLabelPrefixesApi } from '@/services/api/telegramLabelPrefixes';
import type {
  LabelPrefixId,
  TelegramLabelPrefixBody,
} from '@/types/telegramLabelPrefix';

export const TELEGRAM_LABEL_PREFIXES_QUERY_KEY = ['telegram-label-prefixes'] as const;
export const LABEL_SUGGEST_DEBOUNCE_MS = 400;

export function useTelegramLabelPrefixes() {
  return useQuery({
    queryKey: TELEGRAM_LABEL_PREFIXES_QUERY_KEY,
    queryFn: telegramLabelPrefixesApi.list,
    staleTime: 30_000,
  });
}

function useInvalidatingMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: TELEGRAM_LABEL_PREFIXES_QUERY_KEY });
    },
  });
}

export function useTelegramLabelPrefixMutations() {
  return {
    create: useInvalidatingMutation((data: TelegramLabelPrefixBody) =>
      telegramLabelPrefixesApi.create(data)
    ),
    update: useInvalidatingMutation((args: { id: LabelPrefixId; data: TelegramLabelPrefixBody }) =>
      telegramLabelPrefixesApi.update(args.id, args.data)
    ),
    remove: useInvalidatingMutation((id: LabelPrefixId) => telegramLabelPrefixesApi.remove(id)),
  };
}

/**
 * Подбор префикса по номеру: запрос после паузы ввода и только для корректного
 * номера. enabled=false (переподключение) - запроса нет.
 */
export function useLabelPrefixSuggest(phone: string, enabled = true) {
  const debounced = useDebounce(phone, LABEL_SUGGEST_DEBOUNCE_MS);
  const valid = enabled && validatePhone(debounced) === null;
  const normalized = valid ? normalizePhone(debounced) : '';
  return useQuery({
    queryKey: ['telegram-label-prefix-suggest', normalized],
    queryFn: () => telegramLabelPrefixesApi.suggest(normalized),
    enabled: valid,
    staleTime: 30_000,
  });
}
